import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	Container,
	Grid,
	Typography,
	Box,
	Paper,
	Pagination,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	Stack,
	ThemeProvider,
	TextField,
	Checkbox,
	Alert,
	createTheme,
	Fade,
	Menu,
	IconButton,
	Tooltip,
	Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ProductCard from "./ProductCard/ProductCard";
import UploadPage from "./UploadPage/UploadPage";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SearchIcon from "@mui/icons-material/Search";
import { debounce } from "lodash";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
// Create custom theme
const theme = createTheme({
	typography: {
		fontFamily:
			'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	},
	shape: {
		borderRadius: 8, // Base border radius for the theme
	},
	palette: {
		primary: {
			main: "#000000",
		},
		text: {
			primary: "#000000",
			secondary: "#666666",
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					textTransform: "none",
					fontWeight: 500,
					fontFamily:
						'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				},
				contained: {
					backgroundColor: "#000",
					color: "#fff",
					"&:hover": {
						backgroundColor: "#333",
					},
				},
				outlined: {
					borderColor: "#000",
					color: "#000",
					"&:hover": {
						borderColor: "#333",
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 12,
					boxShadow: "none",
					border: "1px solid #e0e0e0",
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 12,
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					fontFamily:
						'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					fontFamily:
						'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#000",
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontFamily:
						'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
					"&.Mui-focused": {
						color: "#000",
					},
				},
			},
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					fontFamily:
						'"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
				},
			},
		},
		MuiGrid: {
			styleOverrides: {
				root: {
					"& .MuiGrid-item": {
						"& > *": {
							borderRadius: 8,
						},
					},
				},
			},
		},
	},
});

const PictorialST = () => {
	const [products, setProducts] = useState([]);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [pdfGenerating, setPdfGenerating] = useState(false);
	const [pdfPopupMessage, setPdfPopupMessage] = useState("");
	const [pdfConflictPopup, setPdfConflictPopup] = useState(false);
	const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
	const [hoveredSort, setHoveredSort] = useState(null);
	const [currentPageDownloadAnchor, setCurrentPageDownloadAnchor] = useState(null);
	const [allPagesDownloadAnchor, setAllPagesDownloadAnchor] = useState(null);
	const [currentPageDownloadOpen, setCurrentPageDownloadOpen] = useState(false);
	const [allPagesDownloadOpen, setAllPagesDownloadOpen] = useState(false);
	const [currentPage, setCurrentPage] = useState("main"); // 'main' or 'upload'
	const [dateRange, setDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [pendingDateRange, setPendingDateRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
	});
	const [totals, setTotals] = useState({
		totalBuy: 0,
		totalSold: 0,
		subCategory: "",
	});
	const [pagination, setPagination] = useState({
		page: 1,
		per_page: 12,
		total_pages: 1,
		total: 0,
		isPageChange: false,
	});
	const [filters, setFilters] = useState({
		department: "",
		subCategory: "",
		brand: "",
		subBrand: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		channel: "",
		subChannel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
		start_date: "",
		end_date: "",
	});
	const [pendingFilters, setPendingFilters] = useState({
		department: "",
		subCategory: "",
		brand: "",
		subBrand: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlend: "",
		channel: "",
		businessChannel: "",
		subChannel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
		start_date: "",
		end_date: "",
	});
	const pdfRef = useRef(null);
	const [currentDateTime, setCurrentDateTime] = useState(new Date());
	const [filterOptions, setFilterOptions] = useState({
		departments: [],
		subCategories: [],
		brands: [],
		subBrands: [],
		styles: [],
		colors: [],
		sleeves: [],
		divisions: [],
		seasons: [],
		fashionability: [],
		occasions: [],
		palettes: [],
		fitTypes: [],
		fabricBlends: [],
		channels: [],
		businessChannels: [],
		subChannels: [],
		stores: [],
		cities: [], // Add test cities
		states: [],
		regions: [],
		tiers: [],
	});

	// Add state for master filter options
	const [masterFilterOptions, setMasterFilterOptions] = useState({});

	// Add debug log after initialization
	useEffect(() => {
		console.log("Initial Filter Options State:", filterOptions);
	}, [filterOptions]);

	const [sortConfig, setSortConfig] = useState({
		field: "st", // Default to ST%
		direction: "desc",
	});
	const [pdfProgress, setPdfProgress] = useState(0);

	// Add new state for search terms and selected items
	const [searchTerms, setSearchTerms] = useState({
		brand: "",
		subBrand: "",
		department: "",
		subCategory: "",
		style: "",
		color: "",
		sleeve: "",
		division: "",
		season: "",
		fashionability: "",
		occasion: "",
		palette: "",
		fitType: "",
		fabricBlends: "",
		channel: "",
		businessChannel: "",
		subChannel: "",
		store: "",
		city: "",
		state: "",
		region: "",
		tier: "",
	});

	const [selectedItems, setSelectedItems] = useState({
		brands: new Set(),
		subBrands: new Set(),
		departments: new Set(),
		subCategories: new Set(),
		styles: new Set(),
		colors: new Set(),
		sleeves: new Set(),
		divisions: new Set(),
		seasons: new Set(),
		fashionability: new Set(),
		occasions: new Set(),
		palettes: new Set(),
		fitTypes: new Set(),
		fabricBlends: new Set(),
		channels: new Set(),
		businessChannels: new Set(),
		subChannels: new Set(),
		stores: new Set(),
		cities: new Set(),
		states: new Set(),
		regions: new Set(),
		tiers: new Set(),
	});

	// Add new state for all products
	const [allProducts, setAllProducts] = useState([]);

	// Add new state for dashboard metrics
	const [dashboardMetrics, setDashboardMetrics] = useState({
		totalProducts: 0,
		averageST: 0,
		totalSales: 0,
		totalInventory: 0,
		onlineContribution: 0,
		offlineContribution: 0,
	});

	// Add new state for collapsible sections
	const [expandedSections, setExpandedSections] = useState({
		date: true,
		product: true,
		channel: true,
		location: true,
	});

	// Add state to track open dropdowns
	const [openDropdowns, setOpenDropdowns] = useState(new Set());

	// Add a ref to store the cancel token
	const cancelTokenRef = useRef(null);

	// Add a ref for tracking the latest request
	const currentRequestIdRef = useRef(0);

	// Add a ref to track if we're in the middle of applying filters
	const isApplyingFiltersRef = useRef(false);

	// Add ref to track if filter options have been loaded
	const filterOptionsLoadedRef = useRef(false);

	// List of dependent filters
	const dependentFilterKeys = [
		"brand",
		"subBrand",
		"department",
		"subCategory",
		"style",
		"color",
		"sleeve",
		"season",
		"division",
		"fashionability",
		"occasion",
		"palette",
		"fitType",
		"fabricBlend",
	];

	// Fetch dependent filter options
	const fetchDependentFilters = useCallback(async (currentFilters) => {
		try {
			const params = {};
			dependentFilterKeys.forEach((key) => {
				if (currentFilters[key] && currentFilters[key].trim() !== "") {
					params[key] = currentFilters[key];
				}
			});
			const response = await pythonVmInstance.get("/st_pictorial/dependent-filters", { params });
			if (response && response.data) {
				setFilterOptions((prev) => ({
					...prev,
					brands: response.data.brands || [],
					subBrands: response.data.subBrands || [],
					departments: response.data.departments || [],
					subCategories: response.data.subCategories || [],
					styles: response.data.styles || [],
					colors: response.data.colors || [],
					sleeves: response.data.sleeves || [],
					seasons: response.data.seasons || [],
					divisions: response.data.divisions || [],
					fashionability: response.data.fashionability || [],
					occasions: response.data.occasions || [],
					palettes: response.data.palettes || [],
					fitTypes: response.data.fitTypes || [],
					fabricBlends: response.data.fabricBlends || [],
				}));
			}
		} catch (err) {
			console.error("Error fetching dependent filters:", err);
		}
	}, []);

	// Watch for changes in dependent filters - only when dropdowns are closed
	useEffect(() => {
		// Only fetch dependent filters if no dropdowns are open
		if (openDropdowns.size === 0) {
			const activeFilters = {};
			dependentFilterKeys.forEach((key) => {
				if (filters[key]) activeFilters[key] = filters[key];
			});
			fetchDependentFilters(activeFilters);
		}
		// eslint-disable-next-line
	}, dependentFilterKeys.map((key) => filters[key]).concat([openDropdowns.size]));

	const fetchFilterOptions = useCallback(async () => {
		if (filterOptionsLoadedRef.current) {
			console.log("Filter options already loaded, skipping fetch");
			return;
		}

		try {
			console.log("Starting to fetch filter options...");
			setFilterOptionsLoading(true);

			const response = await pythonVmInstance.get("/st_pictorial/filter-options");
			console.log("Raw API Response:", response);

			if (!response || !response.data) {
				console.error("Invalid API response:", response);
				throw new Error("Invalid API response");
			}

			const data = response.data;
			console.log("Filter Options API Response Data:", data);
			console.log("Raw SubCategories from API:", data.subCategories);
			console.log("SubCategories type:", typeof data.subCategories);

			// Process subCategories with more detailed handling
			let subCategories = [];
			if (Array.isArray(data.subCategories)) {
				console.log("SubCategories is an array");
				subCategories = data.subCategories;
			} else if (typeof data.subCategories === "string") {
				console.log("SubCategories is a string, attempting to parse");
				try {
					subCategories = JSON.parse(data.subCategories);
				} catch (e) {
					console.error("Failed to parse subCategories string:", e);
				}
			} else if (data.subCategories && typeof data.subCategories === "object") {
				console.log("SubCategories is an object, converting to array");
				// If it's an object with SUB_CATEGORY property
				if (data.subCategories.SUB_CATEGORY) {
					subCategories = Array.isArray(data.subCategories.SUB_CATEGORY)
						? data.subCategories.SUB_CATEGORY
						: [data.subCategories.SUB_CATEGORY];
				} else {
					subCategories = Object.values(data.subCategories);
				}
			}

			// Ensure subCategories are unique and non-empty
			subCategories = [...new Set(subCategories)].filter(Boolean);

			console.log("Processed subCategories:", subCategories);
			console.log("SubCategories length:", subCategories.length);

			// Process cities (existing code)
			let cities = [];
			if (Array.isArray(data.cities)) {
				cities = data.cities;
			} else if (typeof data.cities === "string") {
				try {
					cities = JSON.parse(data.cities);
				} catch (e) {
					console.error("Failed to parse cities string:", e);
				}
			} else if (data.cities) {
				cities = Object.values(data.cities);
			}

			setFilterOptions((prev) => {
				const newOptions = {
					...prev,
					departments: data.departments || [],
					subCategories: subCategories,
					brands: data.brands || [],
					subBrands: data.subBrands || [],
					styles: data.styles || [],
					colors: data.colors || [],
					sleeves: data.sleeves || [],
					divisions: data.divisions || [],
					seasons: data.seasons || [],
					fashionability: data.fashionability || [],
					occasions: data.occasions || [],
					palettes: data.palettes || [],
					fitTypes: data.fitTypes || [],
					fabricBlends: data.fabricBlends || [],
					channels: data.channels || [],
					businessChannels: data.businessChannels || [],
					subChannels: data.subChannels || [],
					stores: data.stores || [],
					cities: data.cities || [],
					states: data.states || [],
					regions: data.regions || [],
					tiers: data.tiers || [],
					// blends will be loaded through dependent filters
				};
				console.log("Final subCategories in new options:", newOptions.subCategories);
				console.log("FitTypes in new options:", newOptions.fitTypes);
				console.log("Raw fitTypes from API:", data.fitTypes);
				console.log("FabricBlends in new options:", newOptions.fabricBlends);
				console.log("Raw fabricBlends from API:", data.fabricBlends);
				return newOptions;
			});

			setMasterFilterOptions({
				departments: data.departments || [],
				subCategories: subCategories,
				brands: data.brands || [],
				subBrands: data.subBrands || [],
				styles: data.styles || [],
				colors: data.colors || [],
				sleeves: data.sleeves || [],
				divisions: data.divisions || [],
				seasons: data.seasons || [],
				fashionability: data.fashionability || [],
				occasions: data.occasions || [],
				palettes: data.palettes || [],
				fitTypes: data.fitTypes || [],
				fabricBlends: data.fabricBlends || [],
				channels: data.channels || [],
				businessChannels: data.businessChannels || [],
				subChannels: data.subChannels || [],
				stores: data.stores || [],
				cities: data.cities || [],
				states: data.states || [],
				regions: data.regions || [],
				tiers: data.tiers || [],

				// blends will be loaded through dependent filters
			});

			filterOptionsLoadedRef.current = true;
			console.log("Filter options loaded successfully");
		} catch (err) {
			console.error("Error fetching filter options:", err);
			setError("Failed to load filter options. Please refresh the page.");
		} finally {
			setFilterOptionsLoading(false);
		}
	}, []);

	const fetchProducts = useCallback(
		async (startDate, endDate) => {
			// Increment request ID at the function scope
			const thisRequestId = ++currentRequestIdRef.current;

			try {
				setLoading(true);
				setError("");

				const formattedStartDate = format(startDate, "yyyy-MM-dd");
				const formattedEndDate = format(endDate, "yyyy-MM-dd");

				const params = {
					start_date: formattedStartDate,
					end_date: formattedEndDate,
					get_all: true,
				};

				Object.entries(filters).forEach(([key, value]) => {
					if (key !== "start_date" && key !== "end_date" && value && value.trim() !== "") {
						const paramKey = key.endsWith("s") ? key.slice(0, -1) : key;
						params[paramKey] = value;
					}
				});

				console.log("API Request Parameters:", JSON.stringify(params, null, 2));

				const response = await pythonVmInstance.get("/st_pictorial/products", {
					params: params,
					timeout: 90000,
				});

				// Check if this is still the latest request
				if (thisRequestId !== currentRequestIdRef.current) {
					console.log("Ignoring stale response for request:", thisRequestId);
					return;
				}

				console.log("Processing response for request:", thisRequestId);

				if (!response.data || !response.data.products) {
					throw new Error("Invalid response format from server");
				}

				const flattenedProducts = response.data.products.flat();
				console.log("Total products fetched:", flattenedProducts.length);

				if (flattenedProducts.length === 0) {
					setError("No data found for the selected filters. Please try changing the date range or filters.");
					setProducts([]);
					setAllProducts([]);
					setPagination((prev) => ({
						...prev,
						total_pages: 0,
						total: 0,
					}));
					return;
				}

				// Process products only if this is still the latest request
				if (thisRequestId === currentRequestIdRef.current) {
					const transformedProducts = flattenedProducts.map((product) => ({
						...product,
						troco_ls: product.troco_ls || {
							t_buy: 0,
							t_inw: 0,
							t_sale: 0,
							st: 0,
							mrp: "0",
						},
						lcogs: product.lcogs || {
							gp_percentage: "0.00%",
							gp_value: "0.00",
							oln_cont: "0%",
							offln_cont: "0%",
							sales_cont: "0%",
						},
					}));

					setAllProducts(transformedProducts);

					const totalPages = Math.ceil(transformedProducts.length / pagination.per_page);
					setPagination((prev) => ({
						...prev,
						total_pages: totalPages,
						total: transformedProducts.length,
						page: 1, // Reset to first page on new data
					}));

					const currentPageProducts = transformedProducts.slice(0, pagination.per_page);
					setProducts(currentPageProducts);

					if (transformedProducts.length > 0) {
						const totalBuy = transformedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_buy || 0), 0);
						const totalSold = transformedProducts.reduce((sum, product) => sum + (product.troco_ls?.t_sale || 0), 0);
						setTotals({
							totalBuy: totalBuy,
							totalSold: totalSold,
							subCategory: filters.subCategory || "All",
						});
					}
				}
			} catch (err) {
				console.error("Error in fetchProducts:", err);
				if (err.response && err.response.status === 500) {
					setError("No data found for the selected filters. Please try changing the date range or filters.");
				} else {
					setError(`Failed to fetch products: ${err.message}`);
				}
				setProducts([]);
				setAllProducts([]);
				setPagination((prev) => ({
					...prev,
					total_pages: 0,
					total: 0,
				}));
			} finally {
				// Only update loading state if this is the latest request
				if (currentRequestIdRef.current === thisRequestId) {
					setLoading(false);
				}
			}
		},
		[filters, pagination.per_page]
	);

	// Clean up cancel token on unmount
	useEffect(() => {
		return () => {
			if (cancelTokenRef.current) {
				cancelTokenRef.current.cancel("Component unmounted");
			}
			// Reset the loaded flag when component unmounts
			filterOptionsLoadedRef.current = false;
		};
	}, []);

	// Initial load effect
	useEffect(() => {
		fetchFilterOptions();
	}, [fetchFilterOptions]);

	useEffect(() => {
		if (!loading) return;
		// Only fetch if it's not a pagination change and not in the middle of applying filters
		if (loading && !pagination.isPageChange && !isApplyingFiltersRef.current) {
			fetchProducts(dateRange.startDate, dateRange.endDate);
		}
	}, [dateRange, filters, fetchProducts, loading, pagination.isPageChange]);

	// In useEffect for initial load, set default sort in filters
	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			sort_field: "SELL_THROUGH",
			sort_direction: "DESC",
		}));
	}, []);

	const handleStartDateChange = (newDate) => {
		setPendingDateRange((prev) => ({
			...prev,
			startDate: newDate,
		}));
	};

	const handleEndDateChange = (newDate) => {
		setPendingDateRange((prev) => ({
			...prev,
			endDate: newDate,
		}));
	};

	const handlePageChange = (event, newPage) => {
		// Don't set loading to true for pagination
		setPagination((prev) => ({ ...prev, page: newPage }));

		// Calculate the correct slice of data for the new page
		const startIndex = (newPage - 1) * pagination.per_page;
		const endIndex = startIndex + pagination.per_page;
		const newPageProducts = allProducts.slice(startIndex, endIndex);

		setProducts(newPageProducts);
	};

	const debouncedFilterChange = useCallback(
		debounce((name, value) => {
			setPendingFilters((prev) => ({ ...prev, [name]: value }));
		}, 300),
		[]
	);

	// Update the handleSearchTermChange function to be more robust
	const handleSearchTermChange = (field, value) => {
		console.log(`Search term change for ${field}:`, value);
		setSearchTerms((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	// Update the getFilteredOptions function to be more consistent
	const getFilteredOptions = (field) => {
		console.log(`Getting filtered options for ${field}`);
		console.log("Current search term:", searchTerms[field]);

		// Special case for fashionability
		if (field === "fashionability") {
			const searchTerm = (searchTerms[field] || "").toLowerCase();
			const options = filterOptions[field] || [];
			return options.filter((option) => option && option.toLowerCase().includes(searchTerm));
		}

		// For other fields
		const optionsKey = field.endsWith("s") ? field : `${field}s`;
		const searchTerm = (searchTerms[field] || "").toLowerCase();
		const options = filterOptions[optionsKey] || [];

		console.log(`Options for ${field}:`, options);

		const filteredOptions = options.filter((option) => option && option.toLowerCase().includes(searchTerm.toLowerCase()));

		console.log(`Filtered options for ${field}:`, filteredOptions);
		return filteredOptions;
	};

	// Update the handleFilterChange function
	const handleFilterChange = (event) => {
		const { name, value, type } = event.target;
		console.log(`Filter change for ${name}:`, value);

		const mappedKey = filterKeyMap[name] || `${name}s`;
		const apiKey = name;

		if (type === "checkbox") {
			setSelectedItems((prev) => {
				const currentSet = new Set(prev[mappedKey] || []);
				if (currentSet.has(value)) {
					currentSet.delete(value);
				} else {
					currentSet.add(value);
				}
				setFilters((prevFilters) => ({
					...prevFilters,
					[apiKey]: currentSet.size > 0 ? Array.from(currentSet).join(",") : "",
				}));
				return { ...prev, [mappedKey]: currentSet };
			});
			return;
		}

		if (value === "") {
			setFilters((prev) => ({ ...prev, [apiKey]: "" }));
			setSelectedItems((prev) => ({ ...prev, [mappedKey]: new Set() }));
			return;
		}

		// For single select, replace the value but preserve other filters
		setFilters((prev) => ({ ...prev, [apiKey]: value }));
		setSelectedItems((prev) => ({ ...prev, [mappedKey]: new Set([value]) }));
	};

	// Add getSelectDisplayValue function
	const getSelectDisplayValue = (field) => {
		const key = filterKeyMap[field] || `${field}s`;
		const selectedSet = selectedItems[key];
		if (!selectedSet || selectedSet.size === 0) return "";

		// Show count if multiple items are selected
		if (selectedSet.size > 1) {
			return `${selectedSet.size} selected`;
		}

		// Show the single item if only one is selected
		return Array.from(selectedSet).join(", ");
	};

	// Add this mapping above createFilterSelect
	const filterKeyMap = {
		city: "cities",
		subCategory: "subCategories",
		palette: "palettes",
		fashionability: "fashionability",
		fitType: "fitTypes",
		fabricBlend: "fabricBlends",
		// add more if needed
	};

	// Update createFilterSelect to use the mapping
	const createFilterSelect = (field, label) => {
		const options = masterFilterOptions[filterKeyMap[field] || `${field}s`] || [];
		const validOptions = filterOptions[filterKeyMap[field] || `${field}s`] || [];
		const searchTerm = searchTerms[field] || "";

		// Debug logging for blend filter
		if (field === "fabricBlend") {
			console.log("Fabric Blend filter debug:", {
				field,
				label,
				masterOptions: options,
				validOptions: validOptions,
				masterFilterOptions: masterFilterOptions.fabricBlends,
				filterOptions: filterOptions.fabricBlends,
				optionsLength: options.length,
				validOptionsLength: validOptions.length,
			});
		}

		// Filter options based on search term (case-insensitive)
		const filteredOptions = options.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()));
		const enabledOptions = filteredOptions.filter((option) => validOptions.includes(option));
		const disabledOptions = filteredOptions.filter((option) => !validOptions.includes(option));
		const mappedKey = filterKeyMap[field] || `${field}s`;
		const selectedSet = selectedItems[mappedKey] || new Set();
		const allSelected = enabledOptions.length > 0 && enabledOptions.every((option) => selectedSet.has(option));
		const someSelected = enabledOptions.some((option) => selectedSet.has(option));

		const isOpen = openDropdowns.has(field);

		const handleSelectAll = (e) => {
			const checked = e.target.checked;
			setSelectedItems((prev) => {
				const newSet = new Set(selectedSet);
				if (checked) {
					enabledOptions.forEach((option) => newSet.add(option));
				} else {
					enabledOptions.forEach((option) => newSet.delete(option));
				}
				setFilters((prevFilters) => ({
					...prevFilters,
					[field]: newSet.size > 0 ? Array.from(newSet).join(",") : "",
				}));
				return { ...prev, [mappedKey]: newSet };
			});
		};

		return (
			<FormControl size="small" fullWidth disabled={loading}>
				<InputLabel>{label}</InputLabel>
				<Select
					name={field}
					value={selectedSet.size > 0 ? "selected" : ""}
					onChange={(e) => {
						if (e.target.value === "") return;
						handleFilterChange(e);
					}}
					onOpen={() => {
						setOpenDropdowns((prev) => new Set(prev).add(field));
					}}
					onClose={() => {
						setOpenDropdowns((prev) => {
							const newSet = new Set(prev);
							newSet.delete(field);
							return newSet;
						});
					}}
					label={label}
					renderValue={(selected) => {
						// When dropdown is open, show placeholder or current selection
						if (isOpen) {
							if (selectedSet.size === 0) return "";
							if (selectedSet.size === 1) {
								return Array.from(selectedSet).join(", ");
							}
							return `${selectedSet.size} selected`;
						}

						// When dropdown is closed, show the selection summary
						if (selectedSet.size === 0) return "";
						if (selectedSet.size === 1) {
							return Array.from(selectedSet).join(", ");
						}
						return `${selectedSet.size} selected`;
					}}
					MenuProps={{
						PaperProps: {
							style: {
								maxHeight: 300,
							},
						},
					}}>
					<MenuItem value="">
						<Box sx={{ width: "100%" }}>
							<TextField
								size="small"
								fullWidth
								placeholder={`Search ${label.toLowerCase()}...`}
								value={searchTerm}
								onChange={(e) => handleSearchTermChange(field, e.target.value)}
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}
							/>
						</Box>
					</MenuItem>
					{/* Select All option */}
					<MenuItem
						value=""
						onClick={(e) => {
							e.preventDefault();
							handleSelectAll({ target: { checked: !allSelected } });
						}}>
						<Checkbox
							indeterminate={someSelected && !allSelected}
							checked={allSelected}
							onChange={handleSelectAll}
							onClick={(e) => e.stopPropagation()}
						/>
						<span style={{ marginLeft: "8px", fontWeight: 500 }}>Select All</span>
					</MenuItem>
					{enabledOptions.map((option) => (
						<MenuItem key={option} value={option}>
							<Checkbox
								checked={selectedSet.has(option)}
								onChange={(e) => handleCheckboxChange(field, option)}
								onClick={(e) => e.stopPropagation()}
							/>
							<span style={{ marginLeft: "8px" }}>{option}</span>
						</MenuItem>
					))}
					{disabledOptions.length > 0 && <Divider />}
					{disabledOptions.map((option) => (
						<MenuItem key={option} value={option} disabled>
							<Checkbox checked={selectedSet.has(option)} disabled onClick={(e) => e.stopPropagation()} />
							<span style={{ marginLeft: "8px", color: "#aaa" }}>{option}</span>
						</MenuItem>
					))}
				</Select>
			</FormControl>
		);
	};

	// Replace all individual filter Select components with the template
	// In the render section, replace the existing Select components with:
	{
		createFilterSelect("department", "Department");
	}
	{
		createFilterSelect("subCategory", "Sub Category");
	}
	{
		createFilterSelect("brand", "Brand");
	}
	{
		createFilterSelect("subBrand", "Sub Brand");
	}
	{
		createFilterSelect("style", "Style");
	}
	{
		createFilterSelect("color", "Color");
	}
	{
		createFilterSelect("sleeve", "Sleeve");
	}
	{
		createFilterSelect("division", "Division");
	}
	{
		createFilterSelect("season", "Season");
	}
	{
		createFilterSelect("fashionability", "Fashionability");
	}
	{
		createFilterSelect("occasion", "Occasion");
	}
	{
		createFilterSelect("palette", "Palettes");
	}
	{
		createFilterSelect("channel", "Channel");
	}
	{
		createFilterSelect("businessChannel", "Business Channel");
	}
	{
		createFilterSelect("subChannel", "Sub Channel");
	}
	{
		createFilterSelect("store", "Store");
	}
	{
		createFilterSelect("city", "City");
	}
	{
		createFilterSelect("state", "State");
	}
	{
		createFilterSelect("region", "Region");
	}
	{
		createFilterSelect("tier", "Tier");
	}
	{
		createFilterSelect("fitType", "Fit Type");
	}
	{
		createFilterSelect("fabricBlend", "Fabric Blend");
	}

	// After the dependent filter group, render the rest of the filters independently
	const independentFilters = ["channel", "businessChannel", "subChannel", "store", "city", "state", "region", "tier"];

	const handleSearch = useCallback(() => {
		setLoading(true);
		setError("");

		try {
			const formattedStartDate = format(pendingDateRange.startDate, "yyyy-MM-dd");
			const formattedEndDate = format(pendingDateRange.endDate, "yyyy-MM-dd");
			const newFilters = {
				start_date: formattedStartDate,
				end_date: formattedEndDate,
			};
			Object.entries(selectedItems).forEach(([field, items]) => {
				const apiKey = field.endsWith("s") ? field.slice(0, -1) : field;
				if (["subCategories", "cities", "fashionability"].includes(field)) {
					const specialKey = field === "subCategories" ? "subCategory" : field === "cities" ? "city" : "fashionability";
					if (items.size > 0) {
						newFilters[specialKey] = Array.from(items).join(",");
					} else {
						delete newFilters[specialKey];
					}
				} else {
					if (items.size > 0) {
						newFilters[apiKey] = Array.from(items).join(",");
					} else {
						delete newFilters[apiKey];
					}
				}
			});
			setFilters(newFilters);
			setDateRange(pendingDateRange);
			setPagination((prev) => ({ ...prev, page: 1 }));
			fetchProducts(pendingDateRange.startDate, pendingDateRange.endDate);
		} catch (err) {
			console.error("Error in handleSearch:", err);
			setError("Error applying filters");
		}
	}, [pendingDateRange, selectedItems, fetchProducts]);

	// Calculate ST%
	const calculateST = useCallback(() => {
		if (totals.totalBuy === 0) return 0;
		return ((totals.totalSold / totals.totalBuy) * 100).toFixed(2);
	}, [totals.totalBuy, totals.totalSold]);

	// Format number without decimals
	const formatNumber = (num) => {
		return Math.round(num).toLocaleString();
	};

	// Format number with 2 decimal places for GP
	const formatGPPercentage = (num) => {
		return Number(num).toFixed(2);
	};

	// Format number with 2 decimal places for GP value
	const formatGPValue = (num) => {
		return Number(num).toLocaleString();
	};

	const handleSort = (field) => {
		setLoading(true);
		setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));

		setSortConfig((prev) => {
			let newField = field;
			let newDirection = "desc";
			if (prev.field === field) {
				newDirection = prev.direction === "desc" ? "asc" : "desc";
			}
			return {
				field: newField,
				direction: newDirection,
			};
		});

		// Format dates for the backend
		const formattedStartDate = format(dateRange.startDate, "yyyy-MM-dd");
		const formattedEndDate = format(dateRange.endDate, "yyyy-MM-dd");

		// Map sort field for backend
		let sort_field = "BILL_QTY";
		if (field === "st") sort_field = "SELL_THROUGH";
		if (field === "qty") sort_field = "BILL_QTY";

		// Use the toggled direction
		const newDirection = sortConfig.field === field && sortConfig.direction === "desc" ? "ASC" : "DESC";

		const newFilters = {
			...filters,
			start_date: formattedStartDate,
			end_date: formattedEndDate,
			sort_field: sort_field,
			sort_direction: newDirection,
		};

		setFilters(newFilters);
		fetchProducts(dateRange.startDate, dateRange.endDate);
	};

	// Add live clock update
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentDateTime(new Date());
		}, 1000);

		return () => clearInterval(timer);
	}, []);

	const createProductCard = (product, { formatNumber }) => {
		const card = document.createElement("div");
		card.style.cssText = `
			width: 340px;
			background-color: #ffffff;
			display: flex;
			flex-direction: column;
			font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
			cursor: pointer;
			padding: 10px;
			box-sizing: border-box;
		`;

		// Image container
		const imageContainer = document.createElement("div");
		imageContainer.style.cssText = `
			width: 100%;
			height: 340px;
			overflow: hidden;
			margin-bottom: 12px;
		`;

		const img = document.createElement("img");
		img.src = product.image_url;
		img.crossOrigin = "anonymous";
		img.style.cssText = `
			width: 100%;
			height: 100%;
			object-fit: cover;
		`;
		imageContainer.appendChild(img);

		// Format MRP and COGS without decimals
		const formatCurrency = (value) => {
			if (!value || value === "N/A") return "N/A";
			// Remove any existing commas and handle string "N/A"
			const cleanValue = value.toString().replace(/,/g, "");
			// Parse the number and round it
			const numValue = Math.round(parseFloat(cleanValue));
			// Check if it's a valid number
			if (isNaN(numValue)) return "N/A";
			// Format with commas but no decimals
			return numValue.toLocaleString("en-IN");
		};

		// Product details
		const details = document.createElement("div");
		details.style.cssText = `
			padding: 10px;
			font-size: 15px;
			line-height: 1.5;
		`;

		details.innerHTML = `
			<div style="font-weight: bold; font-size: 16px; margin-bottom: 8px;">OPTION: ${product.PKEY || "N/A"}</div>
			<div style="display: flex; gap: 32px;">
				<div style="flex: 1;">
					<div style="margin-bottom: 4px;">Season: ${product.season || "N/A"}</div>
					<div style="margin-bottom: 4px;">BLEND: ${product.blend || "N/A"}</div>
					<div style="margin-bottom: 4px;">MRP: ${formatCurrency(product.troco_ls?.mrp)}</div>
					<div style="margin-bottom: 4px;">T.INW: ${formatNumber(product.troco_ls?.t_inw || 0)}</div>
					<div style="margin-bottom: 4px;">T.SALE: ${formatNumber(product.troco_ls?.t_sale || 0)}</div>
					<div style="margin-bottom: 4px;">ST: ${((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%</div>
					<div style="margin-bottom: 4px;">Online Cont: ${product.lcogs?.oln_cont || "0%"}</div>
					<div>Offline Cont: ${product.lcogs?.offln_cont || "100%"}</div>
				</div>
				<div style="flex: 1;">
					<div style="margin-bottom: 4px;">COGS: ${formatCurrency(product.COGS)}</div>
					<div style="margin-bottom: 4px;">GP%: ${formatGPPercentage(product.lcogs?.gp_percentage || "0.00%")}</div>
					<div style="margin-bottom: 4px;">GP Value: ${formatGPValue(product.lcogs?.gp_value || 0)}</div>
					<div style="margin-bottom: 4px;">Sales Cont: ${product.lcogs?.sales_cont || "0%"}</div>
					<div>INV: ${product.INV || ""}</div>
				</div>
			</div>
		`;

		card.appendChild(imageContainer);
		card.appendChild(details);

		return card;
	};

	const handleDownloadPDF = useCallback(
		async (downloadType = "all") => {
			if (pdfGenerating) {
				setPdfConflictPopup(true);
				setTimeout(() => setPdfConflictPopup(false), 3000);
				return;
			}
			try {
				console.log("Starting PDF download process...");
				setPdfGenerating(true);
				setPdfProgress(0);
				setPdfPopupMessage("");

				const productsToProcess = downloadType === "current" ? products : allProducts;
				if (!productsToProcess || productsToProcess.length === 0) {
					throw new Error("No products available to download");
				}

				// Create PDF in landscape mode
				const pdf = new jsPDF({
					orientation: "landscape",
					unit: "mm",
					format: "a2", // Use A2 for even more space
				});

				const PRODUCTS_PER_ROW = 6; // Reduced from 8 to 6 for larger cards
				const ROWS_PER_PAGE = 2;
				const PRODUCTS_PER_PAGE = PRODUCTS_PER_ROW * ROWS_PER_PAGE;
				const totalPages = Math.ceil(productsToProcess.length / PRODUCTS_PER_PAGE);

				// Create small popup notification on the right side
				const popupNotification = document.createElement("div");
				popupNotification.style.cssText = `
					position: fixed;
					top: 20px;
					right: 20px;
					width: 300px;
					background: white;
					border-radius: 12px;
					box-shadow: 0 4px 20px rgba(0,0,0,0.15);
					z-index: 9999;
					font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
					border: 1px solid #e0e0e0;
					overflow: hidden;
					animation: slideInRight 0.3s ease-out;
				`;

				// Add CSS animation for slide-in effect
				const style = document.createElement("style");
				style.textContent = `
					@keyframes slideInRight {
						from {
							transform: translateX(100%);
							opacity: 0;
						}
						to {
							transform: translateX(0);
							opacity: 1;
						}
					}
				`;
				document.head.appendChild(style);

				// Progress content
				const progressContent = document.createElement("div");
				progressContent.style.cssText = `
					padding: 20px;
					text-align: center;
				`;
				popupNotification.appendChild(progressContent);

				// Add progress bar container
				const progressBarContainer = document.createElement("div");
				progressBarContainer.style.cssText = `
					width: 100%;
					height: 4px;
					background: #e0e0e0;
					border-radius: 2px;
					overflow: hidden;
					margin-top: 10px;
				`;
				popupNotification.appendChild(progressBarContainer);

				// Add progress bar
				const progressBar = document.createElement("div");
				progressBar.style.cssText = `
					height: 100%;
					background: #000;
					width: 0%;
					transition: width 0.3s ease;
					border-radius: 2px;
				`;
				progressBarContainer.appendChild(progressBar);

				document.body.appendChild(popupNotification);

				// Create temporary container for rendering
				const tempContainer = document.createElement("div");
				tempContainer.style.cssText = `
					position: absolute;
					left: -9999px;
					width: 2400px;
					background: white;
					padding: 20px;
				`;
				document.body.appendChild(tempContainer);

				for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
					const progress = Math.round((pageNum / totalPages) * 100);
					setPdfProgress(progress);
					progressContent.innerHTML = `
						<h3 style="margin: 0 0 10px 0; color: #333; font-size: 16px;">Generating PDF</h3>
						<p style="margin: 5px 0; color: #666; font-size: 14px;">Processing page ${pageNum} of ${totalPages}</p>
						<p style="margin: 5px 0; color: #000; font-weight: 600; font-size: 14px;">${progress}% complete</p>
					`;
					progressBar.style.width = `${progress}%`;

					const startIdx = (pageNum - 1) * PRODUCTS_PER_PAGE;
					const endIdx = Math.min(startIdx + PRODUCTS_PER_PAGE, productsToProcess.length);
					const pageProducts = productsToProcess.slice(startIdx, endIdx);

					// Create grid for this page
					const gridContainer = document.createElement("div");
					gridContainer.style.cssText = `
						display: grid;
						grid-template-columns: repeat(${PRODUCTS_PER_ROW}, 1fr);
						gap: 20px;
						padding: 20px;
						background: white;
					`;

					// Add products to grid
					pageProducts.forEach((product) => {
						const card = createProductCard(product, { formatNumber });
						gridContainer.appendChild(card);
					});

					tempContainer.innerHTML = "";
					tempContainer.appendChild(gridContainer);

					// Wait for images to load
					await new Promise((resolve) => {
						const images = tempContainer.getElementsByTagName("img");
						let loadedImages = 0;
						const totalImages = images.length;

						if (totalImages === 0) {
							resolve();
							return;
						}

						const imageLoaded = () => {
							loadedImages++;
							if (loadedImages === totalImages) {
								resolve();
							}
						};

						Array.from(images).forEach((img) => {
							if (img.complete) {
								imageLoaded();
							} else {
								img.onload = imageLoaded;
								img.onerror = imageLoaded;
							}
							img.crossOrigin = "anonymous";
						});
					});

					try {
						const canvas = await html2canvas(gridContainer, {
							scale: 2,
							useCORS: true,
							allowTaint: true,
							backgroundColor: "#ffffff",
							logging: true,
							width: gridContainer.offsetWidth,
							height: gridContainer.offsetHeight,
						});

						if (pageNum > 1) {
							pdf.addPage();
						}

						// Add header
						addHeader(pdf, {
							currentDateTime,
							dateRange,
							totals,
							calculateST,
							formatNumber,
							pageWidth: pdf.internal.pageSize.getWidth(),
							filters,
						});

						const pageWidth = pdf.internal.pageSize.getWidth();
						const pageHeight = pdf.internal.pageSize.getHeight();
						const margin = 20;
						const availableHeight = pageHeight - 45 - margin * 2;

						const scale = Math.min((pageWidth - margin * 2) / canvas.width, availableHeight / canvas.height);

						const scaledWidth = canvas.width * scale;
						const scaledHeight = canvas.height * scale;

						// Center the content
						const x = (pageWidth - scaledWidth) / 2;
						const y = 45 + (availableHeight - scaledHeight) / 2;

						pdf.addImage(canvas.toDataURL("image/jpeg", 1.0), "JPEG", x, y, scaledWidth, scaledHeight);
					} catch (error) {
						console.error(`Error processing page ${pageNum}:`, error);
						throw error;
					}
				}

				// Clean up
				document.body.removeChild(popupNotification);
				document.body.removeChild(tempContainer);

				const fileName =
					downloadType === "current"
						? `sell-through-report-page-${pagination.page}.pdf`
						: `sell-through-report-all-pages.pdf`;

				pdf.save(fileName);
				console.log("PDF saved successfully");
			} catch (error) {
				console.error("Error in PDF generation:", error);
				setError(`Failed to generate PDF: ${error.message}`);
				// Clean up in case of error
				const popup = document.querySelector('div[style*="right: 20px"]');
				if (popup) document.body.removeChild(popup);
				const tempContainer = document.querySelector('div[style*="left: -9999px"]');
				if (tempContainer) document.body.removeChild(tempContainer);
			} finally {
				setPdfGenerating(false);
				setPdfProgress(0);
				setPdfPopupMessage("");
			}
		},
		[products, allProducts, pagination.page, dateRange, totals, calculateST, formatNumber, currentDateTime, filters]
	);

	// Helper function to add header to PDF
	const filterLabels = {
		brand: "Brand",
		subBrand: "Sub Brand",
		department: "Department",
		subCategory: "Sub Category",
		style: "Style",
		color: "Color",
		sleeve: "Sleeve",
		division: "Division",
		season: "Season",
		fashionability: "Fashionability",
		occasion: "Occasion",
		palette: "Palette",
		fitType: "Fit Type",
		fabricBlends: "Fabric Blend",
		channel: "Channel",
		businessChannel: "Business Channel",
		subChannel: "Sub Channel",
		store: "Store",
		city: "City",
		state: "State",
		region: "Region",
		tier: "Tier",
	};

	const addHeader = (pdf, { currentDateTime, dateRange, totals, calculateST, formatNumber, pageWidth, filters }) => {
		// Add black rectangle at the top
		pdf.setFillColor(0, 0, 0);
		pdf.rect(0, 0, pageWidth, 15, "F");

		// Add title in white
		pdf.setTextColor(255, 255, 255);
		pdf.setFont("helvetica", "bold");
		pdf.setFontSize(24);
		pdf.text("SELL THROUGH PICTORIAL", pageWidth / 2, 11, {
			align: "center",
			renderingMode: "fill",
		});

		// Reset text color to black for rest of the content
		pdf.setTextColor(0, 0, 0);
		pdf.setFontSize(11);
		pdf.setFont("helvetica", "normal");

		// Add header content in two columns
		const leftMargin = 15;
		const rightMargin = pageWidth - 80;
		const startY = 25;
		const lineHeight = 7;

		// Left column
		pdf.text(`Date: ${format(currentDateTime, "dd-MM-yyyy HH:mm:ss")}`, leftMargin, startY);
		pdf.text(
			`Period: ${format(dateRange.startDate, "dd-MM-yyyy")} - ${format(dateRange.endDate, "dd-MM-yyyy")}`,
			leftMargin,
			startY + lineHeight
		);
		pdf.text(
			`Sub Category: ${totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}`,
			leftMargin,
			startY + lineHeight * 2
		);

		// Right column
		pdf.setFont(undefined, "bold");
		pdf.text(`Total Buy: `, rightMargin, startY);
		pdf.text(`Total Sold: `, rightMargin, startY + lineHeight);
		pdf.text(`ST%: `, rightMargin, startY + lineHeight * 2);
		pdf.setFont(undefined, "normal");
		pdf.text(`${formatNumber(totals.totalBuy)}`, rightMargin + 25, startY);
		pdf.text(`${formatNumber(totals.totalSold)}`, rightMargin + 25, startY + lineHeight);
		pdf.text(`${calculateST()}%`, rightMargin + 25, startY + lineHeight * 2);

		// Print selected filters below header
		let filterY = startY + lineHeight * 4;
		const filterEntries = Object.entries(filters)
			.filter(([key, value]) => filterLabels[key] && value && value !== "")
			.map(([key, value]) => `${filterLabels[key]}: ${value}`);

		// Print up to 3 filters per line
		const filtersPerLine = 3;
		for (let i = 0; i < filterEntries.length; i += filtersPerLine) {
			const line = filterEntries.slice(i, i + filtersPerLine).join("    |    ");
			pdf.text(line, leftMargin, filterY);
			filterY += lineHeight;
		}
	};

	// Add handler for checkbox selection
	const handleCheckboxChange = (field, value) => {
		const mappedKey = filterKeyMap[field] || `${field}s`;
		const apiKey = field;
		setSelectedItems((prev) => {
			const currentSet = prev[mappedKey] || new Set();
			const newSet = new Set(currentSet);
			if (newSet.has(value)) {
				newSet.delete(value);
			} else {
				newSet.add(value);
			}
			setFilters((prevFilters) => ({
				...prevFilters,
				[apiKey]: newSet.size > 0 ? Array.from(newSet).join(",") : "",
			}));
			return { ...prev, [mappedKey]: newSet };
		});
	};

	// Add a function to handle applying filters
	const applyFilters = () => {
		setLoading(true);
		setPagination((prev) => ({ ...prev, page: 1, isPageChange: false }));
		setError("");

		try {
			isApplyingFiltersRef.current = true;
			const formattedStartDate = format(pendingDateRange.startDate, "yyyy-MM-dd");
			const formattedEndDate = format(pendingDateRange.endDate, "yyyy-MM-dd");
			const newFilters = {
				start_date: formattedStartDate,
				end_date: formattedEndDate,
			};

			console.log("Current selectedItems:", selectedItems);

			Object.entries(selectedItems).forEach(([field, items]) => {
				const apiKey = field.endsWith("s") ? field.slice(0, -1) : field;
				if (["subCategories", "cities", "fashionability"].includes(field)) {
					const specialKey = field === "subCategories" ? "subCategory" : field === "cities" ? "city" : "fashionability";
					if (items.size > 0) {
						newFilters[specialKey] = Array.from(items).join(",");
						console.log(`Adding filter ${specialKey}:`, Array.from(items).join(","));
					} else {
						delete newFilters[specialKey];
					}
				} else {
					if (items.size > 0) {
						newFilters[apiKey] = Array.from(items).join(",");
						console.log(`Adding filter ${apiKey}:`, Array.from(items).join(","));
					} else {
						delete newFilters[apiKey];
					}
				}
			});

			console.log("Final filters to be applied:", newFilters);
			setFilters(newFilters);
			setDateRange(pendingDateRange);
			fetchProducts(pendingDateRange.startDate, pendingDateRange.endDate);
		} catch (err) {
			console.error("Error applying filters:", err);
			setError("Error applying filters");
		} finally {
			setTimeout(() => {
				isApplyingFiltersRef.current = false;
			}, 100);
		}
	};

	// Add reset function
	const handleResetFilters = () => {
		// Reset search terms
		setSearchTerms({
			brand: "",
			subBrand: "",
			department: "",
			subCategory: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			channel: "",
			businessChannel: "",
			subChannel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
		});

		// Reset pending filters
		setPendingFilters({
			department: "",
			subCategory: "",
			brand: "",
			subBrand: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			channel: "",
			businessChannel: "",
			subChannel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
		});

		// Reset selected items
		setSelectedItems({
			brands: new Set(),
			subBrands: new Set(),
			departments: new Set(),
			subCategories: new Set(),
			styles: new Set(),
			colors: new Set(),
			sleeves: new Set(),
			divisions: new Set(),
			seasons: new Set(),
			fashionability: new Set(),
			occasions: new Set(),
			palettes: new Set(),
			fitTypes: new Set(),
			fabricBlends: new Set(),
			channels: new Set(),
			businessChannels: new Set(),
			subChannels: new Set(),
			stores: new Set(),
			cities: new Set(),
			states: new Set(),
			regions: new Set(),
			tiers: new Set(),
		});

		// Reset filters but keep the date range
		const { start_date, end_date } = filters;
		setFilters({
			department: "",
			subCategory: "",
			brand: "",
			subBrand: "",
			style: "",
			color: "",
			sleeve: "",
			division: "",
			season: "",
			fashionability: "",
			occasion: "",
			palette: "",
			fitType: "",
			fabricBlend: "",
			channel: "",
			businessChannel: "",
			subChannel: "",
			store: "",
			city: "",
			state: "",
			region: "",
			tier: "",
			start_date,
			end_date,
		});

		// Reset pagination to first page
		setPagination((prev) => ({ ...prev, page: 1 }));
	};

	// Loading Indicator with transparent background
	const LoadingOverlay = () => (
		<Box
			sx={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "rgba(255, 255, 255, 0.7)",
				zIndex: 1300,
				backdropFilter: "blur(3px)",
			}}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 2,
				}}>
				<CircularProgress variant="indeterminate" sx={{ color: "#000" }} size={60} />
				<Typography sx={{ color: "#000", fontWeight: 500 }}>Loading...</Typography>
			</Box>
		</Box>
	);

	// Add new state for collapsible sections
	const handleSectionToggle = (section) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	const handleDownloadCSV = useCallback(
		async (downloadType = "all") => {
			try {
				console.log("Starting CSV download process...");
				setLoading(true);

				let productsToProcess;
				let fileName;

				// Download current page or all pages
				productsToProcess = downloadType === "current" ? products : allProducts;
				fileName =
					downloadType === "current"
						? `sell-through-report-page-${pagination.page}.csv`
						: `sell-through-report-all-pages.csv`;

				if (!productsToProcess || productsToProcess.length === 0) {
					throw new Error("No data available to download");
				}

				// Define CSV headers for product data
				const headers = [
					"URL",
					"Option",
					"Brand",
					"Sub Brand",
					"Department",
					"Sub Category",
					"Style",
					"Color",
					"Sleeve",
					"Season",
					"Division",
					"Fashionability",
					"Occasion",
					"Palette Type",
					"Fit Type",
					"Fabric Blend",
					"MRP",
					"COGS",
					"T.INW",
					"T.SALE",
					"ST%",
					"Online Cont%",
					"Offline Cont%",
					"GP%",
					"GP Value",
					"Sales Cont%",
					"INV",
				];

				// Convert data to CSV rows
				const csvRows = [
					headers.join(","), // Header row
					...productsToProcess.map((product) => {
						// For product data, use the predefined mapping
						return [
							`"${product.image_url || "N/A"}"`,
							`"${product.PKEY || "N/A"}"`,
							`"${product.brand || "N/A"}"`,
							`"${product.subBrand || "N/A"}"`,
							`"${product.category || "N/A"}"`,
							`"${product.subCategory || "N/A"}"`,
							`"${product.style || "N/A"}"`,
							`"${product.color || "N/A"}"`,
							`"${product.sleeve || "N/A"}"`,
							`"${product.season || "N/A"}"`,
							`"${product.division || "N/A"}"`,
							`"${product.fashionability || "N/A"}"`,
							`"${product.occasion || "N/A"}"`,
							`"${product.paletteType || "N/A"}"`,
							`"${product.fitType || "N/A"}"`,
							`"${product.fabricBlends || "N/A"}"`,
							`"${product.troco_ls?.mrp || "N/A"}"`,
							`"${product.COGS || "N/A"}"`,
							`"${product.troco_ls?.t_inw || "0"}"`,
							`"${product.troco_ls?.t_sale || "0"}"`,
							`"${((product.troco_ls?.t_sale / product.troco_ls?.t_buy) * 100 || 0).toFixed(2)}%"`,
							`"${product.lcogs?.oln_cont || "0%"}"`,
							`"${product.lcogs?.offln_cont || "100%"}"`,
							`"${product.lcogs?.gp_percentage || "0.00%"}"`,
							`"${product.lcogs?.gp_value || "0"}"`,
							`"${product.lcogs?.sales_cont || "0%"}"`,
							`"${product.INV || ""}"`,
						].join(",");
					}),
				];

				// Create CSV content
				const csvContent = csvRows.join("\n");

				// Create and download the file
				const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				const url = URL.createObjectURL(blob);
				link.setAttribute("href", url);
				link.setAttribute("download", fileName);
				link.style.visibility = "hidden";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				console.log("CSV saved successfully");
			} catch (error) {
				console.error("Error in CSV generation:", error);
				setError(`Failed to generate CSV: ${error.message}`);
			} finally {
				setLoading(false);
			}
		},
		[products, allProducts, pagination.page]
	);

	// Download dropdown handlers
	const handleCurrentPageDownloadClick = (event) => {
		setCurrentPageDownloadAnchor(event.currentTarget);
		setCurrentPageDownloadOpen(!currentPageDownloadOpen);
	};

	const handleAllPagesDownloadClick = (event) => {
		setAllPagesDownloadAnchor(event.currentTarget);
		setAllPagesDownloadOpen(!allPagesDownloadOpen);
	};

	const handleDownloadMenuClose = () => {
		setCurrentPageDownloadAnchor(null);
		setAllPagesDownloadAnchor(null);
		setCurrentPageDownloadOpen(false);
		setAllPagesDownloadOpen(false);
	};

	// Navigation functions
	const navigateToUpload = () => {
		setCurrentPage("upload");
	};

	const navigateToMain = () => {
		setCurrentPage("main");
	};

	// Function to handle applying filters from upload page
	const handleApplyFiltersFromUpload = (filters, dateRange) => {
		try {
			// Set the date range
			setDateRange(dateRange);
			setPendingDateRange(dateRange);

			// Convert filter values to selectedItems format
			const newSelectedItems = { ...selectedItems };
			const newFilters = {
				start_date: format(dateRange.startDate, "yyyy-MM-dd"),
				end_date: format(dateRange.endDate, "yyyy-MM-dd"),
			};

			// Map filter values to selectedItems and filters
			Object.entries(filters).forEach(([key, value]) => {
				if (key === "start_date" || key === "end_date") return; // Skip date fields

				const values = value
					.split(",")
					.map((v) => v.trim())
					.filter((v) => v);
				if (values.length > 0) {
					// Map filter key to selectedItems key
					let selectedKey;
					switch (key) {
						case "subCategory":
							selectedKey = "subCategories";
							break;
						case "city":
							selectedKey = "cities";
							break;
						case "palette":
							selectedKey = "palettes";
							break;
						case "fashionability":
							selectedKey = "fashionability";
							break;
						default:
							selectedKey = `${key}s`;
					}

					newSelectedItems[selectedKey] = new Set(values);

					// Also update the filters state immediately
					const apiKey = key;
					newFilters[apiKey] = values.join(",");
				}
			});

			// Set both selectedItems and filters
			setSelectedItems(newSelectedItems);
			setFilters(newFilters);

			console.log("CSV filters applied - selectedItems:", newSelectedItems);
			console.log("CSV filters applied - filters:", newFilters);

			// Navigate to main page
			setCurrentPage("main");

			// Don't auto-apply filters - let user make manual selections first
			// User will need to click the search button to apply all filters
		} catch (error) {
			console.error("Error applying filters from upload:", error);
		}
	};

	// Warn on page/tab close during PDF generation
	useEffect(() => {
		if (pdfGenerating) {
			window.onbeforeunload = (e) => {
				e.preventDefault();
				e.returnValue = "A PDF is still being generated. Are you sure you want to leave this page?";
				return e.returnValue;
			};
		} else {
			window.onbeforeunload = null;
		}
	}, [pdfGenerating]);

	return (
		<ThemeProvider theme={theme}>
			<Container maxWidth="xl" sx={{ pt: 1 }}>
				{currentPage === "upload" ? (
					<UploadPage onBack={navigateToMain} onApplyFilters={handleApplyFiltersFromUpload} />
				) : filterOptionsLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "80vh",
							flexDirection: "column",
							gap: 2,
						}}>
						<CircularProgress size={60} sx={{ color: "#000" }} />
						<Typography variant="h6" sx={{ color: "#000" }}>
							Loading data...
						</Typography>
					</Box>
				) : (
					<Paper sx={{ p: 3, mb: 4, backgroundColor: "#fff" }}>
						{/* Header division */}
						<Grid container spacing={2} sx={{ mb: 3 }}>
							{/* Left Side - Date and Period */}
							<Grid item xs={12} md={6}>
								<Box>
									<Typography variant="h6" sx={{ color: "#000", fontWeight: 600, mb: 2 }}>
										SELL THROUGH PICTORIAL
									</Typography>
									<Typography variant="body1" sx={{ mb: 1, color: "#000" }}>
										DATE: {format(currentDateTime, "dd-MM-yyyy HH:mm:ss")}
									</Typography>
									<Typography variant="body1" sx={{ mb: 2, color: "#000" }}>
										PERIOD: {format(dateRange.startDate, "dd-MM-yyyy")} - {format(dateRange.endDate, "dd-MM-yyyy")}
									</Typography>
								</Box>
							</Grid>

							{/* Right Side - Summary Stats */}
							<Grid item xs={12} md={6}>
								<Box sx={{ textAlign: "right" }}>
									<Typography variant="body1" sx={{ mb: 1, color: "#000" }}>
										<Box component="span" sx={{ fontWeight: "bold" }}>
											SUB CATEGORY:{" "}
										</Box>
										{totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}
									</Typography>
									<Typography variant="body1" sx={{ mb: 1, color: "#000" }}>
										<Box component="span" sx={{ fontWeight: "bold" }}>
											Total buy:{" "}
										</Box>
										{formatNumber(totals.totalBuy)}
									</Typography>
									<Typography variant="body1" sx={{ mb: 1, color: "#000" }}>
										<Box component="span" sx={{ fontWeight: "bold" }}>
											Total sold:{" "}
										</Box>
										{formatNumber(totals.totalSold)}
									</Typography>
									<Typography variant="body1" sx={{ mb: 1, color: "#000" }}>
										<Box component="span" sx={{ fontWeight: "bold" }}>
											ST%:{" "}
										</Box>
										{calculateST()}%
									</Typography>
								</Box>
							</Grid>
						</Grid>

						{/* Enhanced Filters Section */}
						{/* Controls Row */}
						<Box sx={{ mb: 2, mt: 3 }}>
							<Grid container justifyContent="space-between" alignItems="center">
								<Grid item>
									<Stack direction="row" spacing={2}>
										<Button
											variant={sortConfig.field === "st" ? "contained" : "outlined"}
											onClick={() => handleSort("st")}
											size="small"
											onMouseEnter={() => setHoveredSort("st")}
											onMouseLeave={() => setHoveredSort(null)}
											sx={{
												position: "relative",
											}}>
											Sort by ST%
											<span
												className="sort-arrow"
												style={{
													opacity: sortConfig.field === "st" || hoveredSort === "st" ? 1 : 0.3,
													transition: "opacity 0.2s ease",
													marginLeft: "4px",
													display: "inline-flex",
													alignItems: "center",
													pointerEvents: "none",
												}}>
												{sortConfig.field === "st" && sortConfig.direction === "desc" ? (
													<KeyboardArrowDownIcon fontSize="small" />
												) : sortConfig.field === "st" && sortConfig.direction === "asc" ? (
													<KeyboardArrowUpIcon fontSize="small" />
												) : (
													<KeyboardArrowDownIcon fontSize="small" />
												)}
											</span>
										</Button>
										<Button
											variant={sortConfig.field === "qty" ? "contained" : "outlined"}
											onClick={() => handleSort("qty")}
											size="small"
											onMouseEnter={() => setHoveredSort("qty")}
											onMouseLeave={() => setHoveredSort(null)}
											sx={{
												position: "relative",
											}}>
											Sort by Qty Sold
											<span
												className="sort-arrow"
												style={{
													opacity: sortConfig.field === "qty" || hoveredSort === "qty" ? 1 : 0.3,
													transition: "opacity 0.2s ease",
													marginLeft: "4px",
													display: "inline-flex",
													alignItems: "center",
													pointerEvents: "none",
												}}>
												{sortConfig.field === "qty" && sortConfig.direction === "desc" ? (
													<KeyboardArrowDownIcon fontSize="small" />
												) : sortConfig.field === "qty" && sortConfig.direction === "asc" ? (
													<KeyboardArrowUpIcon fontSize="small" />
												) : (
													<KeyboardArrowDownIcon fontSize="small" />
												)}
											</span>
										</Button>
										<Button
											variant="outlined"
											onClick={handleResetFilters}
											size="small"
											startIcon={<RefreshIcon />}
											sx={{
												borderColor: "#ff4444",
												color: "#ff4444",
												"&:hover": {
													borderColor: "#ff0000",
													backgroundColor: "rgba(255, 0, 0, 0.04)",
												},
											}}>
											Reset Filters
										</Button>
										<Button
											variant="outlined"
											onClick={navigateToUpload}
											size="small"
											startIcon={<UploadFileIcon />}
											sx={{
												borderColor: "#28a745",
												color: "#28a745",
												"&:hover": {
													borderColor: "#218838",
													backgroundColor: "rgba(40, 167, 69, 0.04)",
												},
											}}>
											CSV Upload
										</Button>
									</Stack>
								</Grid>
								<Grid item>
									<Stack direction="row" spacing={1}>
										<Button
											variant="outlined"
											onClick={handleCurrentPageDownloadClick}
											disabled={loading}
											size="small"
											endIcon={currentPageDownloadOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
											Download Current Page
										</Button>
										<Menu
											anchorEl={currentPageDownloadAnchor}
											open={Boolean(currentPageDownloadAnchor)}
											onClose={handleDownloadMenuClose}
											anchorOrigin={{
												vertical: "bottom",
												horizontal: "left",
											}}
											transformOrigin={{
												vertical: "top",
												horizontal: "left",
											}}>
											<MenuItem
												onClick={() => {
													handleDownloadPDF("current");
													handleDownloadMenuClose();
												}}>
												As PDF
											</MenuItem>
											<MenuItem
												onClick={() => {
													handleDownloadCSV("current");
													handleDownloadMenuClose();
												}}>
												As CSV
											</MenuItem>
										</Menu>
										<Button
											variant="contained"
											onClick={handleAllPagesDownloadClick}
											disabled={loading}
											size="small"
											endIcon={allPagesDownloadOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}>
											Download All Pages
										</Button>
										<Menu
											anchorEl={allPagesDownloadAnchor}
											open={Boolean(allPagesDownloadAnchor)}
											onClose={handleDownloadMenuClose}
											anchorOrigin={{
												vertical: "bottom",
												horizontal: "left",
											}}
											transformOrigin={{
												vertical: "top",
												horizontal: "left",
											}}>
											<MenuItem
												onClick={() => {
													handleDownloadPDF("all");
													handleDownloadMenuClose();
												}}>
												As PDF
											</MenuItem>
											<MenuItem
												onClick={() => {
													handleDownloadCSV("all");
													handleDownloadMenuClose();
												}}>
												As CSV
											</MenuItem>
										</Menu>
									</Stack>
								</Grid>
							</Grid>
						</Box>

						{/* Date Picker and Filters Row */}
						<Box sx={{ mb: 3 }}>
							{error && (
								<Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError("")}>
									{error}
								</Alert>
							)}
							<Grid container spacing={2} alignItems="center">
								{/* Date Pickers */}
								<Grid item xs={12} sm={4}>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<Grid container spacing={2}>
											<Grid item xs={6}>
												<DatePicker
													label="Start Date"
													value={pendingDateRange.startDate}
													onChange={handleStartDateChange}
													slotProps={{ textField: { size: "small", fullWidth: true } }}
													format="dd-MM-yyyy"
												/>
											</Grid>
											<Grid item xs={6}>
												<DatePicker
													label="End Date"
													value={pendingDateRange.endDate}
													onChange={handleEndDateChange}
													minDate={pendingDateRange.startDate}
													slotProps={{ textField: { size: "small", fullWidth: true } }}
													format="dd-MM-yyyy"
												/>
											</Grid>
										</Grid>
									</LocalizationProvider>
								</Grid>

								{/* Filters */}
								<Grid item xs={12} sm={8}>
									<Box sx={{ mb: 3, position: "relative" }}>
										<Grid
											container
											spacing={1}
											alignItems="center"
											sx={{ mb: 2, p: 0, backgroundColor: "transparent" }}>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("brand", "Brand")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("subBrand", "Sub Brand")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("department", "Department")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("subCategory", "Sub Category")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("style", "Style")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("color", "Color")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("sleeve", "Sleeve")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("season", "Season")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("division", "Division")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("fashionability", "Fashionability")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("occasion", "Occasion")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("palette", "Palettes")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("fitType", "Fit Type")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("fabricBlend", "Fabric Blend")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("channel", "Channel")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("businessChannel", "Business Channel")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("subChannel", "Sub Channel")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("store", "Store")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("city", "City")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("state", "State")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("region", "Region")}
											</Grid>
											<Grid item xs={12} sm={2}>
												{createFilterSelect("tier", "Tier")}
											</Grid>
										</Grid>
										{filterOptionsLoading && (
											<Box
												sx={{
													position: "absolute",
													top: 0,
													left: 0,
													right: 0,
													bottom: 0,
													bgcolor: "rgba(255,255,255,0.6)",
													zIndex: 10,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}>
												<CircularProgress size={32} />
											</Box>
										)}
									</Box>
									<Grid container spacing={1} alignItems="center">
										<Grid item xs={12} sm={0.5}>
											<Button
												variant="contained"
												onClick={applyFilters}
												sx={{
													height: "40px",
													minWidth: "40px",
													width: "40px",
													padding: 0,
													backgroundColor: "#000",
													"&:hover": {
														backgroundColor: "#333",
													},
												}}>
												<SearchIcon />
											</Button>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Box>

						{/* Loading Indicator */}
						{loading && <LoadingOverlay />}

						{/* Products Grid - This division will be captured for PDF */}
						<Box ref={pdfRef}>
							<Fade in={!loading} timeout={200}>
								<Grid container spacing={3} justifyContent="flex-start" sx={{ minHeight: "600px" }}>
									{products.map((product, index) => (
										<Grid item key={`${product.PKEY}-${pagination.page}-${index}`} xs={12} sm={6} md={3}>
											<Fade
												in={true}
												timeout={400}
												style={{
													transitionDelay: `${index * 30}ms`,
													transitionTimingFunction: "ease-out",
												}}>
												<div>
													<ProductCard product={product} />
												</div>
											</Fade>
										</Grid>
									))}
								</Grid>
							</Fade>
							{loading && <LoadingOverlay />}
						</Box>

						<Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
							<Pagination
								count={pagination.total_pages}
								page={pagination.page}
								onChange={handlePageChange}
								color="primary"
								size="large"
								siblingCount={2}
								showFirstButton
								showLastButton
							/>
						</Box>

						{pdfConflictPopup && (
							<div
								style={{
									position: "fixed",
									top: 150,
									right: 20,
									background: "#fff",
									color: "#000",
									border: "1px solid #e0e0e0",
									borderRadius: 8,
									boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
									padding: "16px 24px",
									zIndex: 10001,
									fontFamily: "Inter, sans-serif",
									fontWeight: 600,
									fontSize: 15,
									minWidth: 260,
									textAlign: "center",
									animation: "slideInRight 0.3s ease-out",
								}}>
								A PDF is already being generated.
								<br />
								Please wait for it to finish.
							</div>
						)}
					</Paper>
				)}
			</Container>
		</ThemeProvider>
	);
};

export default PictorialST;
